import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { LocationSwatch } from './SpaceSwatch'
import { DropLine, RowCount, SectionHeader, rowClass, sidebarDragPreview } from './SidebarRows'
import { canReorderProject, resolveProjectDrop, rowZone } from './sidebarDnd'
import { spaceOpenCount, useOpenTaskCounts } from './useSpaceCounts'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { SpaceDropPosition, SpaceReorderEvent } from '@/routes/features/projects/hooks/useSpaceReorder'
import { ProjectIcon, projectAccentColor } from '@/lib/utils/projectIcon'
import { useProjects } from '@/lib/db/rxdb/hooks'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { createDragExtractor } from '@/lib/utils/dragDrop'
import { cn } from '@/lib/utils'

const extractSpaceDragData = createDragExtractor('space')

interface SpacesListProps {
  spaces: ReadonlyArray<ProjectDocument>
  isLoading: boolean
  /** The space the route is in, marked so you can see where you are. */
  routeSpaceId: string | null
  onDrillIn: (spaceId: string) => void
  onAddSpace: () => void
  /** Spaces in sidebar display order; private ranks only reorder among these. */
  orderedIds: ReadonlyArray<string>
  draggingId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (event: SpaceReorderEvent) => void
  dropIndicator: { id: string; position: SpaceDropPosition } | null
  onDropIndicatorChange: (indicator: { id: string; position: SpaceDropPosition } | null) => void
}

/**
 * Top level of the drill-in sidebar (design 21 C): one row per space with its
 * colour, name and open-task count. Clicking a row drills in. Dragging a row
 * writes a private per-user rank (MOL-9) — never the shared Project.sortOrder.
 */
export function SpacesList({
  spaces,
  isLoading,
  routeSpaceId,
  onDrillIn,
  onAddSpace,
  orderedIds,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
}: SpacesListProps) {
  const { t } = useTranslation('projects')
  const { t: tNav } = useTranslation('navigation')
  const { projects } = useProjects()
  const counts = useOpenTaskCounts()

  return (
    <section aria-label={t('sidebar.yourSpaces')} className="py-1.5">
      <SectionHeader label={t('sidebar.yourSpaces')} onAdd={onAddSpace} addLabel={t('sidebar.addSpaceAria')} />
      {!isLoading && spaces.length === 0 ? (
        <p className="px-[18px] py-1.5 text-sm text-muted-foreground">{t('sidebar.noSpaces')}</p>
      ) : (
        <ul className="flex flex-col gap-px px-2">
          {spaces.map((space) => {
            const count = spaceOpenCount(space, projects, counts)
            return (
              <li key={space.id}>
                <SpaceRow
                  space={space}
                  count={count}
                  isRouteSpace={routeSpaceId === space.id}
                  openLabel={tNav('spaceNav.openSpace', { name: space.name })}
                  onDrillIn={onDrillIn}
                  orderedIds={orderedIds}
                  draggingId={draggingId}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onReorder={onReorder}
                  dropIndicator={dropIndicator}
                  onDropIndicatorChange={onDropIndicatorChange}
                />
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function SpaceRow({
  space,
  count,
  isRouteSpace,
  openLabel,
  onDrillIn,
  orderedIds,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
}: {
  space: ProjectDocument
  count: number
  isRouteSpace: boolean
  openLabel: string
  onDrillIn: (spaceId: string) => void
  orderedIds: ReadonlyArray<string>
  draggingId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (event: SpaceReorderEvent) => void
  dropIndicator: { id: string; position: SpaceDropPosition } | null
  onDropIndicatorChange: (indicator: { id: string; position: SpaceDropPosition } | null) => void
}) {
  const rowRef = useRef<HTMLButtonElement>(null)
  const isDragging = draggingId === space.id
  const indicator = dropIndicator?.id === space.id ? dropIndicator.position : null

  useEffect(() => {
    const element = rowRef.current
    if (!element) return
    const spaceIds = new Set(orderedIds)
    // Reuse the project drop helpers — they only care about ordered ids / edges.
    const positionFor = (sourceId: string, clientY: number) =>
      resolveProjectDrop(orderedIds, sourceId, space.id, rowZone(clientY, element.getBoundingClientRect()).edge)
    return combine(
      draggable({
        element,
        getInitialData: () => ({ type: 'space', id: space.id }),
        onGenerateDragPreview: ({ nativeSetDragImage }) =>
          sidebarDragPreview(nativeSetDragImage, element, space.name),
        onDragStart: () => {
          onDragStart(space.id)
          onDropIndicatorChange(null)
        },
        onDrop: () => {
          onDragEnd()
          onDropIndicatorChange(null)
        },
      }),
      dropTargetForElements({
        element,
        // Only Space rows are valid sources. A project dragged from a drill-in
        // must not land here — its order is the shared Project.sortOrder.
        canDrop: ({ source }) => {
          const data = extractSpaceDragData(source.data)
          return data !== null && canReorderProject(data.id, space.id, spaceIds)
        },
        getData: () => ({ type: 'space', id: space.id }),
        onDrag: ({ location, source }) => {
          const data = extractSpaceDragData(source.data)
          if (!data) {
            onDropIndicatorChange(null)
            return
          }
          const position = positionFor(data.id, location.current.input.clientY)
          onDropIndicatorChange(position ? { id: space.id, position } : null)
        },
        onDragLeave: () => {
          onDropIndicatorChange(null)
        },
        onDrop: ({ location, source }) => {
          onDropIndicatorChange(null)
          const data = extractSpaceDragData(source.data)
          if (!data) return
          const position = positionFor(data.id, location.current.input.clientY)
          if (position) {
            onReorder({ sourceId: data.id, targetId: space.id, dropPosition: position })
          }
        },
      }),
    )
  }, [
    space.id,
    space.name,
    orderedIds,
    onDragStart,
    onDragEnd,
    onReorder,
    onDropIndicatorChange,
  ])

  return (
    <button
      ref={rowRef}
      type="button"
      data-testid="sidebar-space-row"
      title={openLabel}
      onClick={() => onDrillIn(space.id)}
      className={rowClass(
        false,
        cn(
          'font-medium cursor-grab active:cursor-grabbing',
          // A dashed slot stays where the row was while it's lifted.
          isDragging &&
            'border border-dashed border-border bg-transparent shadow-none hover:bg-transparent [&>*]:invisible',
        ),
      )}
    >
      {indicator && <DropLine edge={indicator === 'before' ? 'top' : 'bottom'} indentPx={8} />}
      <span data-drag-icon className="flex shrink-0">
        <LocationSwatch location={withAccentColor(space)} size={14} />
      </span>
      <span className="min-w-0 flex-1 truncate">{space.name}</span>
      {isRouteSpace && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-brand" />}
      {count > 0 && <RowCount>{count}</RowCount>}
      <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground', count === 0 && 'ml-auto')} />
    </button>
  )
}

/**
 * Every Space reads as a coloured tile, including one whose chosen Lucide icon
 * has no colour. Uncoloured, it rendered as a bare grey glyph — in the rail
 * that looked like a nav item such as Home rather than a Space (MOL-42). The
 * glyph itself is untouched; only the tile takes the Space's stable accent.
 */
function withAccentColor<T extends { iconColor: string | null; name: string }>(space: T): T {
  return space.iconColor ? space : { ...space, iconColor: projectAccentColor(space) }
}

/**
 * Rail (icon) mode: spaces as colour squares. Clicking one opens that space;
 * the sidebar then drills in when it's docked or peeked. Read-only order —
 * private ranks apply via the sorted `spaces` prop; no DnD in the rail.
 */
export function SpacesRail({
  spaces,
  workspaceSlug,
  routeSpaceId,
}: {
  spaces: ReadonlyArray<ProjectDocument>
  workspaceSlug: string
  routeSpaceId: string | null
}) {
  const { t } = useTranslation('projects')
  if (spaces.length === 0) return null
  return (
    <nav aria-label={t('sidebar.yourSpaces')} className="flex flex-col items-center gap-1 border-t border-sidebar-border py-2">
      {spaces.map((space) => (
        <Tooltip key={space.id} delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to="/$workspaceSlug/spaces/$spaceKey"
              params={{ workspaceSlug, spaceKey: space.projectKey.toLowerCase() }}
              aria-label={space.name}
              aria-current={routeSpaceId === space.id ? 'true' : undefined}
              className={cn(
                'flex size-8 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent',
                routeSpaceId === space.id && 'bg-sidebar-active shadow-[0_0_0_1px_var(--sidebar-border)]',
              )}
            >
              <ProjectIcon
                iconType={space.iconType}
                icon={space.icon}
                iconColor={withAccentColor(space).iconColor}
                size={18}
                badgeIconSize={14}
                variant="badge"
                fallbackLabel={space.name}
                className="shrink-0"
              />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">{space.name}</TooltipContent>
        </Tooltip>
      ))}
    </nav>
  )
}
